import { type Registry as Services, service } from '@ember/service';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type TeamConversationAdapter from 'uplisting-frontend/adapters/team-conversation';

export const TEAM_INBOX_PAGE_SIZE = 20;

export type TeamInboxFilter = 'all' | 'task' | 'booking' | 'mentioned';

export interface ITeamInboxPage {
  conversations: ModelRegistry['team-conversation'][];
  cursor?: string;
  hasMore: boolean;
  totalCount: number;
}

interface ITeamInboxQuery {
  limit: number;
  cursor?: string;
  resource_type?: Extract<TeamInboxFilter, 'task' | 'booking'>;
  mentioned?: boolean;
}

interface ITeamInboxMeta {
  cursor?: string | null;
  has_more?: boolean;
  total_count?: number;
}

export default class TeamConversationRepositoryService extends BaseRepositoryService<
  ModelRegistry['team-conversation']
> {
  @service session!: Services['session'];

  recordName = 'team-conversation';
  implementMethods = ['query', 'findRecord'];

  public async forUser({
    filter,
    cursor,
  }: {
    filter: TeamInboxFilter;
    cursor?: string;
  }): Promise<ITeamInboxPage> {
    const params: ITeamInboxQuery = { limit: TEAM_INBOX_PAGE_SIZE };

    if (cursor) {
      params.cursor = cursor;
    }

    if (filter === 'mentioned') {
      params.mentioned = true;
    } else if (filter !== 'all') {
      params.resource_type = filter;
    }

    const result = await this.query(params);
    // @ts-expect-error - have no good typings for the meta
    const meta: ITeamInboxMeta = result.meta ?? {};

    return {
      conversations: result.slice(),
      cursor: meta.cursor ?? undefined,
      hasMore: meta.has_more ?? false,
      totalCount: meta.total_count ?? result.length,
    };
  }

  public async anchoredTo(
    resourceType: string,
    resourceId: string,
  ): Promise<ModelRegistry['team-conversation'] | undefined> {
    const conversations = await this.query({
      anchor_resource_type: resourceType,
      anchor_resource_id: resourceId,
    });

    return conversations.slice()[0];
  }

  public async markRead(
    conversation: ModelRegistry['team-conversation'],
  ): Promise<void> {
    if (!conversation.unread) {
      return;
    }

    try {
      const adapter = this.store.adapterFor(
        'team-conversation',
      ) as TeamConversationAdapter;
      const payload = await adapter.markRead(conversation.id);

      this.store.pushPayload(payload);
      this.decrementUnreadCount();
    } catch (err) {
      this.sentry.captureException(err, {
        tags: { repository: this.recordName, operation: 'markRead' },
      });
    }
  }

  private decrementUnreadCount(): void {
    const user = this.session.currentUser;

    if (!user) {
      return;
    }

    user.unreadTeamMessages = Math.max(0, (user.unreadTeamMessages ?? 0) - 1);
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/team-conversation': TeamConversationRepositoryService;
  }
}
